import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { usePrefersReducedMotion } from '@hooks';

const ScrollEffects = createGlobalStyle`
  @keyframes trace-section {
    from { transform: scaleX(0); filter: brightness(2); }
    to { transform: scaleX(1); filter: brightness(1); }
  }
  .retro-in-view .numbered-heading::after {
    transform-origin: left;
    background: linear-gradient(90deg, #3c9a95, var(--lightest-navy));
    animation: trace-section 1s ease-out both;
  }
  .project-inner::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border: 1px solid transparent;
    border-radius: var(--border-radius);
    transition: border-color 220ms, box-shadow 220ms;
  }
  .project-inner:hover::after, .project-inner:focus-within::after {
    border-color: #34796f;
    box-shadow: inset 0 0 22px #64ffda08;
  }
  .project-overline::before {
    content: '';
    display: inline-block;
    width: 5px;
    height: 5px;
    margin: 0 9px 2px 0;
    background: var(--green);
    box-shadow: 0 0 8px #64ffda50;
  }
  @media (prefers-reduced-motion: reduce) {
    .retro-in-view .numbered-heading::after { animation: none; }
    .project-inner::after { transition: none; }
  }
`;

const Progress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 90;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #278db8, var(--green));
  box-shadow: 0 0 10px #64ffda50;
  transform: scaleX(0);
  transform-origin: left;
  pointer-events: none;
`;

const Landmark = styled.div`
  position: fixed;
  right: 72px;
  bottom: 26px;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 15px;
  border: 1px solid #235251;
  background: #041015ed;
  box-shadow: 3px 3px 0 #0e292d;
  color: var(--lightest-slate);
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.07em;
  pointer-events: none;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transform: translateY(${p => (p.$visible ? '0' : '8px')});
  transition: opacity 220ms, transform 220ms;
  .marker {
    width: 5px;
    height: 5px;
    background: var(--green);
    box-shadow: 0 0 8px #64ffda55;
  }
  .number {
    color: #57b9c1;
  }
  @media (max-width: 768px), (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const landmarks = {
  about: ['01', 'ABOUT ME'],
  jobs: ['02', 'EXPERIENCE'],
  projects: ['03', 'PROJECTS'],
  contact: ['04', 'SAY HELLO'],
};

const RetroScroll = () => {
  const progressRef = useRef(null);
  const [landmark, setLandmark] = useState(null);
  const [visible, setVisible] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    let frame;
    let timer;
    let lastSection = null;
    const sections = [...document.querySelectorAll('main section[id]')];
    const updateProgress = () => {
      const length = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = length > 0 ? Math.max(0, Math.min(1, window.scrollY / length)) : 0;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${ratio})`;
      }
      frame = null;
    };
    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(updateProgress);
      }
    };
    let observer = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            entry.target.classList.toggle('retro-in-view', entry.isIntersecting);
            const id = entry.target.id;
            if (entry.isIntersecting && landmarks[id] && lastSection !== id && !reducedMotion) {
              lastSection = id;
              setLandmark(landmarks[id]);
              setVisible(true);
              window.clearTimeout(timer);
              timer = window.setTimeout(() => setVisible(false), 1600);
            }
          });
        },
        { rootMargin: '-8% 0px -35% 0px', threshold: 0.08 },
      );
    }
    sections.forEach(section => observer?.observe(section));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateProgress();
    return () => {
      observer?.disconnect();
      sections.forEach(section => section.classList.remove('retro-in-view'));
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [reducedMotion]);

  return (
    <>
      <ScrollEffects />
      <Progress ref={progressRef} aria-hidden="true" />
      <Landmark $visible={visible} aria-hidden="true">
        <span className="marker" />
        <span className="number">{landmark?.[0]}</span>
        {landmark?.[1]}
      </Landmark>
    </>
  );
};
export default RetroScroll;
